import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@common/auth/use-auth';
import { TextField } from '@common/ui/forms/input-field/text-field/text-field';
import { Button } from '@common/ui/buttons/button';
import { Form } from '@ui/forms/form';
import { useForm, Controller } from 'react-hook-form';
import { message } from '@ui/i18n/message';
import { toast } from '@ui/toast/toast';
import { Trans } from '@ui/i18n/trans';

interface ShowFormData {
  show_name: string;
  show_description: string;
  show_date: string;
  show_time: string;
  show_location: string;
  show_ticket_link: string;
  media_type: 'banner' | 'video' | '';
  show_banner: File | null;
  show_video: File | null;
}

export function PageAgendaShowsEdit() {
  const { showId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [mediaType, setMediaType] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loggedInUser, setLoggedInUser] = useState<any>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ShowFormData>();

  // Fetch logged in user
  useEffect(() => {
    const fetchLoggedInUser = async () => {
      try {
        const response = await fetch('https://vibe.sintonizy.com/api/v1/users/me', {
          headers: {
            'Authorization': 'Bearer 1|vRqh3zQF5hC69jECxE1XI4o3iHED33cIQ9glQIlYe755d403',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch logged in user');
        }

        const data = await response.json();
        setLoggedInUser(data.user);
      } catch (error) {
        console.error('Error fetching logged in user:', error);
        toast.danger(message('Failed to authenticate user'));
        navigate('/web/agenda-shows');
      }
    };

    fetchLoggedInUser();
  }, [navigate]);

  useEffect(() => {
    const fetchShowDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!showId) {
          throw new Error('Show ID is required');
        }

        const response = await fetch(`/api/agenda-shows.php?show_id=${showId}`);
        const data = await response.json();

        if (data.status === 'success' && data.data) {
          const show = data.data;

          // Check if logged in user is the owner of the show
          if (loggedInUser && show.user_id !== loggedInUser.id) {
            toast.danger(message("You don't have permission to edit this show"));
            navigate('/web/agenda-shows');
            return;
          }

          // Determine media type based on which field is present
          const initialMediaType = show.show_banner ? 'banner' : show.show_video ? 'video' : '';
          setMediaType(initialMediaType);

          // Reset form with show data
          reset({
            show_name: show.show_name || '',
            show_description: show.show_description || '',
            show_date: show.show_date || '',
            show_time: show.show_time || '',
            show_location: show.show_location || '',
            show_ticket_link: show.show_ticket_link || '',
            media_type: initialMediaType,
            show_banner: null,
            show_video: null,
          });

          // Set preview URL if media exists
          if (show.show_banner) {
            setPreviewUrl(show.show_banner);
          } else if (show.show_video) {
            setPreviewUrl(show.show_video);
          }
        } else {
          throw new Error(data.message || 'Failed to fetch show details');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        toast.danger(message(errorMessage));
        navigate('/web/agenda-shows');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch show details if we have the logged in user
    if (showId && loggedInUser) {
      fetchShowDetails();
    }
  }, [showId, reset, navigate, loggedInUser]);

  const onSubmit = async (values: ShowFormData) => {
    try {
      setSubmitting(true);
      setError(null);

      if (!showId) {
        throw new Error('Show ID is required');
      }

      if (!loggedInUser) {
        throw new Error('You must be logged in to edit shows');
      }

      console.log('Submitting form with values:', values);

      const formData = new FormData();
      formData.append('show_id', showId);
      formData.append('show_name', values.show_name);
      formData.append('show_description', values.show_description || '');
      formData.append('show_date', values.show_date);
      formData.append('show_time', values.show_time);
      formData.append('show_location', values.show_location);
      formData.append('show_ticket_link', values.show_ticket_link || '');
      formData.append('media_type', values.media_type || '');

      // Log FormData contents
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }

      if (values.show_banner instanceof File) {
        formData.append('show_banner', values.show_banner);
      }
      if (values.show_video instanceof File) {
        formData.append('show_video', values.show_video);
      }

      const response = await fetch('/api/agenda-shows.php', {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
        },
        body: formData
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (data.status === 'success') {
        toast.positive(message('Show updated successfully'));
        navigate('/web/agenda-shows');
      } else {
        throw new Error(data.message || 'Failed to update show');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      console.error('Error updating show:', errorMessage);
      setError(errorMessage);
      toast.danger(message(errorMessage));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="container mx-auto p-16 max-w-[1024px]">
      <h1 className="text-2xl font-bold mb-32">
        <Trans message="Edit Show" />
      </h1>
      
      <div className="space-y-20">
        <Controller
          name="show_name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label={<Trans message="Show Name" />}
              errorMessage={errors.show_name?.message}
            />
          )}
        />
        
        <Controller
          name="show_description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={<Trans message="Description" />}
              inputElementType="textarea"
              rows={5}
            />
          )}
        />
        
        <Controller
          name="show_date"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              type="date"
              label={<Trans message="Date" />}
              errorMessage={errors.show_date?.message}
            />
          )}
        />
        
        <Controller
          name="show_time"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              type="time"
              label={<Trans message="Time" />}
              errorMessage={errors.show_time?.message}
            />
          )}
        />
        
        <Controller
          name="show_location"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label={<Trans message="Location" />}
              errorMessage={errors.show_location?.message}
            />
          )}
        />
        
        <Controller
          name="show_ticket_link"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={<Trans message="Ticket Link" />}
            />
          )}
        />
        
        <Controller
          name="media_type"
          control={control}
          render={({ field }) => (
            <div className="form-group">
              <label className="block text-sm font-medium mb-2">
                <Trans message="Media Type" />
              </label>
              <select
                {...field}
                className="form-select"
                onChange={(e) => {
                  field.onChange(e);
                  setMediaType(e.target.value);
                }}
              >
                <option value="">Select Media Type</option>
                <option value="banner">Banner</option>
                <option value="video">Video</option>
              </select>
            </div>
          )}
        />

        <div className="media-upload">
          {mediaType === 'banner' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  <Trans message="Upload Banner" />
                </label>
                <Controller
                  name="show_banner"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          field.onChange(file);
                          setPreviewUrl(URL.createObjectURL(file));
                        }
                      }}
                    />
                  )}
                />
              </div>
            </>
          )}

          {mediaType === 'video' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  <Trans message="Upload Video" />
                </label>
                <Controller
                  name="show_video"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          field.onChange(file);
                          setPreviewUrl(URL.createObjectURL(file));
                        }
                      }}
                    />
                  )}
                />
              </div>
            </>
          )}

          {previewUrl && (
            <div className="preview mt-4">
              <h3 className="text-lg font-medium mb-2">
                <Trans message="Preview" />
              </h3>
              {mediaType === 'banner' ? (
                <img src={previewUrl} alt="Preview" className="max-w-full h-auto" />
              ) : (
                <video src={previewUrl} controls className="max-w-full" />
              )}
            </div>
          )}
        </div>

        <div className="mt-20">
          <Button
            variant="flat"
            color="primary"
            size="md"
            type="submit"
            disabled={submitting}
          >
            {submitting ? <Trans message="Updating..." /> : <Trans message="Update Show" />}
          </Button>
        </div>
      </div>
    </Form>
  );
}
